import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { IconMangel, IconSchild } from '../components/icons';
import { Bildmarke, Schriftzug } from '../components/Marke';
import { DEMO } from '../lib/api';

/**
 * Öffentliche Mängel-Meldeseite für Mieter (kein Login).
 *
 * Erreichbar unter /melden/:token. Der Token verweist auf ein Objekt (optional eine Einheit)
 * eines Vermieters. Die Seite ruft ausschließlich die öffentlichen Portal-Endpunkte auf und
 * sendet keinen Bearer-Token. Sie zeigt bewusst nur Objekt/Einheit an, keine Vermieter- oder
 * Nachbardaten (DSGVO).
 */

const API_BASIS = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/$/, '');

const feldKlasse =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent';

const KATEGORIEN: { wert: Kategorie; label: string }[] = [
  { wert: 'heizung', label: 'Heizung / Warmwasser' },
  { wert: 'wasser', label: 'Wasser / Sanitär' },
  { wert: 'elektro', label: 'Elektro / Licht' },
  { wert: 'schluessel', label: 'Schlüssel / Schloss' },
  { wert: 'gemeinschaft', label: 'Gemeinschaftsflächen' },
  { wert: 'sonstiges', label: 'Sonstiges' },
];

type Kategorie = 'heizung' | 'wasser' | 'elektro' | 'schluessel' | 'gemeinschaft' | 'sonstiges';

interface Kopf {
  objektBezeichnung: string | null;
  adresse: string | null;
  einheitBezeichnung: string | null;
}

function dateiZuBase64(datei: File): Promise<{ base64: string; name: string; mime: string }> {
  return new Promise((resolve, reject) => {
    const leser = new FileReader();
    leser.onload = () => resolve({ base64: String(leser.result), name: datei.name, mime: datei.type });
    leser.onerror = () => reject(new Error('Datei konnte nicht gelesen werden.'));
    leser.readAsDataURL(datei);
  });
}

export function Meldeseite({ token }: { token: string }) {
  const [kopf, setKopf] = useState<Kopf | null>(DEMO ? DEMO_KOPF : null);
  const [laden, setLaden] = useState(!DEMO);
  const [ungueltig, setUngueltig] = useState(false);
  const [gesendet, setGesendet] = useState(false);

  useEffect(() => {
    if (DEMO) return;
    let aktiv = true;
    fetch(`${API_BASIS}/portal/${encodeURIComponent(token)}`)
      .then((res) => {
        if (!res.ok) throw new Error('ungueltig');
        return res.json();
      })
      .then((d: Kopf) => aktiv && setKopf(d))
      .catch(() => aktiv && setUngueltig(true))
      .finally(() => aktiv && setLaden(false));
    return () => {
      aktiv = false;
    };
  }, [token]);

  return (
    <div className="min-h-dvh bg-bg text-fg">
      <div className="mx-auto flex max-w-lg flex-col px-gutter py-8">
        <div className="mb-6 flex items-center gap-3">
          <Bildmarke groesse={36} puls="dezent" className="text-fg" />
          <div>
            <p className="text-sm"><Schriftzug /></p>
            <p className="text-xs text-fg-muted">Mangel melden</p>
          </div>
        </div>

        {laden ? (
          <p className="text-sm text-fg-muted">Wird geladen …</p>
        ) : ungueltig ? (
          <Card>
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-surface-2 text-fg-subtle">
                <IconMangel groesse={22} />
              </span>
              <p className="text-sm font-semibold text-fg">Dieser Link ist nicht mehr gültig.</p>
              <p className="text-sm text-fg-muted">
                Bitte wenden Sie sich direkt an Ihre Hausverwaltung, um einen Mangel zu melden.
              </p>
            </div>
          </Card>
        ) : gesendet ? (
          <Card>
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-soft text-accent">
                <IconSchild groesse={22} />
              </span>
              <p className="text-sm font-semibold text-fg">Ihre Meldung ist eingegangen.</p>
              <p className="text-sm text-fg-muted">
                Die Hausverwaltung wurde informiert und kümmert sich um Ihr Anliegen. Sie können dieses
                Fenster schließen.
              </p>
              <Button variante="sekundaer" groesse="sm" onClick={() => setGesendet(false)}>
                Weiteren Mangel melden
              </Button>
            </div>
          </Card>
        ) : (
          <MeldeFormular token={token} kopf={kopf} onGesendet={() => setGesendet(true)} />
        )}

        <p className="mt-6 text-center text-xs text-fg-subtle">
          Ihre Angaben werden ausschließlich zur Bearbeitung des gemeldeten Mangels verwendet.
        </p>
      </div>
    </div>
  );
}

const DEMO_KOPF: Kopf = {
  objektBezeichnung: 'Lindenstraße 14',
  adresse: 'Lindenstraße 14, 45127 Essen',
  einheitBezeichnung: '2. OG rechts',
};

function MeldeFormular({
  token,
  kopf,
  onGesendet,
}: {
  token: string;
  kopf: Kopf | null;
  onGesendet: () => void;
}) {
  const [kategorie, setKategorie] = useState<Kategorie>('sonstiges');
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [melder, setMelder] = useState('');
  const [foto, setFoto] = useState<File | null>(null);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!titel.trim()) {
      setFehler('Bitte beschreiben Sie den Mangel kurz.');
      return;
    }
    setSendet(true);
    try {
      let fotoDaten: { base64: string; name: string; mime: string } | null = null;
      if (foto) fotoDaten = await dateiZuBase64(foto);
      if (!DEMO) {
        const res = await fetch(`${API_BASIS}/portal/${encodeURIComponent(token)}/mangel`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            kategorie,
            titel: titel.trim(),
            beschreibung: beschreibung.trim() || undefined,
            melder: melder.trim() || undefined,
            fotoBase64: fotoDaten?.base64,
            fotoName: fotoDaten?.name,
            fotoMime: fotoDaten?.mime,
          }),
        });
        if (!res.ok) throw new Error('fehler');
      }
      onGesendet();
    } catch {
      setFehler('Die Meldung konnte nicht übermittelt werden. Bitte versuchen Sie es erneut.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <Card>
      {(kopf?.objektBezeichnung || kopf?.adresse) && (
        <div className="mb-4 rounded-lg bg-surface-2 px-3 py-2.5">
          <p className="text-sm font-semibold text-fg">{kopf?.objektBezeichnung}</p>
          {kopf?.adresse && <p className="text-xs text-fg-muted">{kopf.adresse}</p>}
          {kopf?.einheitBezeichnung && (
            <p className="mt-0.5 text-xs text-fg-muted">Einheit: {kopf.einheitBezeichnung}</p>
          )}
        </div>
      )}

      <form onSubmit={absenden} className="space-y-4">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-fg">Worum geht es?</span>
          <select className={feldKlasse} value={kategorie} onChange={(e) => setKategorie(e.target.value as Kategorie)}>
            {KATEGORIEN.map((k) => (
              <option key={k.wert} value={k.wert}>
                {k.label}
              </option>
            ))}
          </select>
        </label>

        <Input
          label="Kurzbeschreibung"
          placeholder="z. B. Heizung wird nicht warm"
          value={titel}
          onChange={(e) => setTitel(e.target.value)}
        />

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-fg">Details (optional)</span>
          <textarea
            className={`${feldKlasse} min-h-[90px]`}
            placeholder="Seit wann besteht das Problem? Wo genau?"
            value={beschreibung}
            onChange={(e) => setBeschreibung(e.target.value)}
          />
        </label>

        <Input
          label="Ihre Wohnung / Ihr Name (optional)"
          placeholder="z. B. Wohnung 2. OG rechts"
          value={melder}
          onChange={(e) => setMelder(e.target.value)}
        />

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-fg">Foto (optional)</span>
          <input
            type="file"
            accept="image/*"
            className="block w-full text-sm text-fg-muted file:mr-3 file:rounded-lg file:border-0 file:bg-accent-soft file:px-3 file:py-2 file:text-sm file:text-accent-soft-fg"
            onChange={(e) => setFoto(e.target.files?.[0] ?? null)}
          />
        </label>

        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}

        <Button type="submit" disabled={sendet} className="w-full">
          {sendet ? 'Wird übermittelt …' : 'Mangel melden'}
        </Button>
      </form>
    </Card>
  );
}

export default Meldeseite;
